import { Box, Tabs, TabList, Tab, TabPanel, tabClasses } from "@mui/joy";
import { useState } from "react";
import { useResponsive } from "../../../../../../hooks";

interface ITabItem {
    name: string;
    icon?: React.ReactNode;
    component?: React.ReactNode;
}

interface INavigationProps {
    tabList: ITabItem[];
    defaultValue?: string;
}

export function Navigation({ tabList, defaultValue }: INavigationProps) {
    const { isMobile, isTablet } = useResponsive();
    const isCompact = isMobile || isTablet;
    const [activeTab, setActiveTab] = useState(
        defaultValue ?? tabList?.[0]?.name ?? "",
    );

    return (
        <Box sx={{ width: "100%" }}>
            <Tabs
                value={activeTab}
                onChange={(_, value) => setActiveTab(value as string)}
                size="sm"
                orientation={isCompact ? "horizontal" : "vertical"}
                sx={{
                    width: "100%",
                    bgcolor: "transparent",
                    "& .MuiTabs-indicator": { display: "none" },
                }}
            >
                <TabList
                    sx={{
                        gap: 0,
                        p: 0,
                        bgcolor: "transparent",
                        border: "none",
                        borderRadius: 0,

                        ...(!isCompact && {
                            width: 72,
                            minWidth: 72,
                            flexShrink: 0,
                            flexDirection: "column",
                            borderRight: "1px solid",
                            borderColor: "divider",
                            alignSelf: "stretch",
                        }),

                        ...(isCompact && {
                            width: "100%",
                            overflowX: "auto",
                            scrollbarWidth: "none",
                            "&::-webkit-scrollbar": { display: "none" },
                            flexDirection: "row",
                            borderBottom: "1px solid",
                            borderColor: "divider",
                        }),

                        [`&& .${tabClasses.root}`]: {
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 0.25,
                            minHeight: 56,
                            px: 0.5,
                            py: 0.75,
                            borderRadius: 0,
                            color: "text.secondary",
                            opacity: 0.8,
                            bgcolor: "transparent",
                            fontWeight: 700,
                            fontSize: "10px",
                            letterSpacing: "0.02em",
                            lineHeight: 1.15,
                            whiteSpace: "nowrap",
                            flex: isCompact ? 1 : undefined,
                            "& svg": {
                                display: "block",
                                strokeWidth: 2.5,
                            },
                            "&:hover": {
                                bgcolor: "rgba(128, 128, 128, 0.08)",
                                opacity: 1,
                            },
                            [`&.${tabClasses.selected}`]: {
                                color: "primary.plainColor",
                                opacity: 1,
                                fontWeight: 800,
                                bgcolor: "rgba(128, 128, 128, 0.08)",
                                ...(!isCompact && {
                                    borderRight: "2.5px solid",
                                    borderColor: "primary.500",
                                }),
                                ...(isCompact && {
                                    borderBottom: "2.5px solid",
                                    borderColor: "primary.500",
                                }),
                                "&::after": { display: "none" },
                            },
                        },
                    }}
                >
                    {tabList?.map((t) => (
                        <Tab key={t.name} value={t.name}>
                            {t.icon}
                            {t.name}
                        </Tab>
                    ))}
                </TabList>

                {tabList?.map((t) => (
                    <TabPanel
                        key={t.name}
                        value={t.name}
                        sx={{
                            p: 0,
                            px: { xs: 2, md: 2.5 },
                            py: 2,
                            minWidth: 0,
                            flex: 1,
                        }}
                    >
                        {t.component}
                    </TabPanel>
                ))}
            </Tabs>
        </Box>
    );
}
